<template>
  <div class="gambit-cardlet" @click="navigateToGambit">
    <div class="cardlet-info">
      <div class="day-time">
        <span class="day" :class="{ 'is-highlight': isNowOrSoon }">{{
          isRelativeDay ? relativeDayLabel : dayLabel
        }}</span>
        <span>&middot;</span>
        <span class="time">{{ startTimeLabel }}</span>
      </div>

      <div v-if="gambitTags.length" class="cardlet-tags">
        <GamChip v-for="(tag, index) in gambitTags" v-bind="tag" :key="`tag-${index}`" is-upper-case />
      </div>

      <div v-if="!isConcluded" class="cardlet-status">
        <template v-if="isUnconfirmedChallenge && !isChallenger">
          <span class="status-respond">Respond</span>
        </template>
        <template v-else-if="isUnconfirmedChallenge && isChallenger">
          <span class="status-pending">Pending</span>
        </template>
        <template v-else>
          <span class="status-confirmed"><GamIcon :name="GamIconName.CHECK" size="10px" is-current /> Confirmed</span>
        </template>
      </div>
    </div>

    <div v-if="!isConcluded" class="cardlet-cta" @click.stop>
      <template v-if="isUnconfirmedChallenge">
        <GamButton
          v-if="!isChallenger"
          class="cta-circle play-cta play-cta-gambit"
          :variant="GamButtonVariant.SLAB"
          :size="GamButtonSize.LARGE"
          :left-icon="GamIconName.PLAY_EMPTY"
          :is-loading="isAccepting"
          :is-disabled="isAccepting"
          is-icon-only
          @click.stop="handleAcceptChallenge"
        />
      </template>

      <template v-else>
        <GamButton
          v-if="showJoinButton"
          class="cta-circle play-cta play-cta-gambit"
          :variant="GamButtonVariant.SLAB"
          :size="GamButtonSize.LARGE"
          :left-icon="GamIconName.PLAY_EMPTY"
          :is-loading="isJoining"
          :is-disabled="isJoining"
          is-icon-only
          @click.stop="handleJoinGambit"
        />

        <GamButton
          v-if="localIsAttending"
          class="cta-circle play-cta play-cta-gambit is-inverted"
          :variant="GamButtonVariant.SLAB"
          :size="GamButtonSize.LARGE"
          :left-icon="GamIconName.PAUSE"
          :is-loading="isLeaving"
          :is-disabled="isLeaving"
          is-icon-only
          @click.stop="handleLeaveGambit"
        />
      </template>
    </div>

    <LeaveGambitConfirmModal
      v-if="isLeaveConfirmOpen"
      :title="leaveConfirmTitle"
      @close="dismissLeaveConfirm"
      @keep="dismissLeaveConfirm"
      @confirm="confirmLeaveGambit"
    />
  </div>
</template>

<script lang="ts">
export interface GambitTearOffType {
  gambit: NextGambitDto;
  timezone?: string | null;
  isRounded?: boolean;
  isPaddingReduced?: boolean;
  showAttendees?: boolean;
}
</script>

<script setup lang="ts">
import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import { GambitAttendanceStatus, type BaseGambitDto } from '@/core/data/gambit/gambit.interface';
import type { NextGambitDto } from '@/core/data/location/location.interface';
import { localize } from '@/core/gambits';
import GambitHelper from '@/core/helpers/gambit.helper';
import PlayerHelper from '@/core/helpers/player.helper';
import { useAuthStore } from '@/stores/auth.store';
import { useGambitStore } from '@/stores/gambit.store';
import GamButton from '@/views/components/GamButton.vue';
import GamChip from '@/views/components/GamChip.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamChipSize, GamChipVariant } from '@/views/composables/constants/components/gamChip.constants';
import { GamDateType } from '@/views/composables/constants/components/gamGambit.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { useLeaveGambitConfirm } from '@/views/composables/hooks/useLeaveGambitConfirm';
import { useUnpairedChip } from '@/views/composables/hooks/useUnpairedChip';
import LeaveGambitConfirmModal from '@/views/main/gambits/modals/LeaveGambitConfirmModal.vue';
import { isAfter } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps<GambitTearOffType>();
const emit = defineEmits<{ 'action-completed': [] }>();

const authStore = useAuthStore();
const { userDetails } = storeToRefs(authStore);

const resolvedTimezone = computed(() => props.timezone || 'UTC');

const startTimeLabel = computed(() => formatInTimeZone(props.gambit.start, resolvedTimezone.value, 'HH:mm'));
const dayLabel = computed(() => formatInTimeZone(props.gambit.start, resolvedTimezone.value, 'EEEE'));

const dateType = computed(() => GambitHelper.getGambitsDateType(props.gambit.start, false, props.gambit.end));

const isRelativeDay = computed(() => dateType.value !== GamDateType.DAY);
const isNowOrSoon = computed(() => dateType.value !== GamDateType.DAY);
const relativeDayLabel = computed(() => localize(`gambit.card.date.time.type.${dateType.value}`));

/* One attendee keeps their own rating, dots included. A group, or a rating
   widened by the elo bounds, is a number or a range. Same chip as a spark gambit. */
const gambitRatingTag = computed(() => {
  const rated = (props.gambit.attendeeAvatars ?? []).filter(
    (attendee) => typeof attendee.rating === 'number' && attendee.rating > 0,
  );
  const bounds = [props.gambit.minEloRating, props.gambit.maxEloRating].filter(
    (rating): rating is number => typeof rating === 'number' && rating > 0,
  );
  const values = [...rated.map((attendee) => attendee.rating as number), ...bounds];
  if (!values.length) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const presentation =
    rated.length === 1 && min === max
      ? PlayerHelper.groupRatingPresentation(rated)
      : { label: min === max ? String(min) : `${min} - ${max}` };
  if (!presentation) return null;

  return {
    ...presentation,
    size: GamChipSize.X_SMALL,
    leftIcon: GamIconName.GAMBIT,
  };
});

const unpairedChip = useUnpairedChip(() => {
  const confirmed = (props.gambit.attendances ?? [])
    .filter((attendance) => attendance.status === GambitAttendanceStatus.CONFIRMED)
    .map((attendance) => attendance.userId);
  if (confirmed.length) return confirmed;
  return (props.gambit.attendeeAvatars ?? []).map((avatar) => avatar.userId);
});

const gambitTags = computed(() => {
  let tags = GambitHelper.getGambitsTags(props.gambit as unknown as BaseGambitDto, {
    size: GamChipSize.X_SMALL,
    showAttendees: true,
    showRating: false,
    showChessSets: false,
    highlightAttendance: true,
  }).map((tag) => (tag.variant === GamChipVariant.PRIMARY ? { ...tag, isActive: true } : tag));

  const rating = gambitRatingTag.value;
  if (rating) {
    const [first, ...rest] = tags;
    tags = first?.leftIcon === GamIconName.PAWN ? [first, rating, ...rest] : [rating, ...tags];
  }

  return unpairedChip.value ? [unpairedChip.value, ...tags] : tags;
});

const localIsAttending = ref(props.gambit.isAttending);
const localIsConfirmed = ref(props.gambit.isConfirmed);

watch(
  () => props.gambit.isAttending,
  (next) => {
    localIsAttending.value = next;
  },
);

watch(
  () => props.gambit.isConfirmed,
  (next) => {
    localIsConfirmed.value = next;
  },
);

const router = useRouter();
const gambitStore = useGambitStore();

const isJoining = ref(false);
const isLeaving = ref(false);
const isAccepting = ref(false);

const isConcluded = computed(() => (props.gambit.end ? isAfter(new Date(), new Date(props.gambit.end)) : false));

const isUnconfirmedChallenge = computed(() => props.gambit.isChallenge && !localIsConfirmed.value);
const isChallenger = computed(() => !!userDetails.value && props.gambit.ownerId === userDetails.value.id);

const showJoinButton = computed(() => {
  return !isConcluded.value && !localIsAttending.value && !isUnconfirmedChallenge.value;
});

const navigateToGambit = () => {
  if (!props.gambit) return;
  router.push({ name: RouterNameType.GAMBIT, params: { id: props.gambit.id }, query: { back: 'history' } });
};

const handleJoinGambit = async () => {
  isJoining.value = true;
  localIsAttending.value = true;

  await gambitStore.joinGambit(props.gambit.id);

  isJoining.value = false;
  emit('action-completed');
};

const { isLeaveConfirmOpen, leaveConfirmTitle, requestLeaveGambit, dismissLeaveConfirm, confirmLeaveGambit } =
  useLeaveGambitConfirm();

const handleLeaveGambit = async () => {
  await requestLeaveGambit(props.gambit, performLeaveGambit);
};

const performLeaveGambit = async () => {
  isLeaving.value = true;
  localIsAttending.value = false;

  await gambitStore.leaveGambit(props.gambit.id);

  isLeaving.value = false;
  emit('action-completed');
};

const handleAcceptChallenge = async () => {
  isAccepting.value = true;

  const outcome = await gambitStore.acceptChallenge(props.gambit.id);

  if (outcome.merged) {
    isAccepting.value = false;
    router.push({
      name: RouterNameType.GAMBIT,
      params: { id: outcome.gambitId },
      query: { back: 'history' },
    });
    return;
  }

  localIsConfirmed.value = true;
  localIsAttending.value = true;
  isAccepting.value = false;
  emit('action-completed');
};
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;

.gambit-cardlet {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.75rem 0;
  gap: 1rem;
  cursor: pointer;
}

.cardlet-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  flex: 1;
}

.day-time {
  display: flex;
  align-items: center;
  gap: 0.3em;
  font-size: 16px;
  letter-spacing: 0.05em;
  white-space: nowrap;
}

.day,
.time {
  text-transform: uppercase;
  font-weight: 400;
  color: var(--color-white);
}

.day.is-highlight {
  color: var(--color-pink);
}

/* Scrolls inside the info column so the CTAs keep their own width. */
.cardlet-tags {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: var(--spacing-xs);
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  > * {
    flex-shrink: 0;
  }
}

.cardlet-status {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 600;
}

.status-confirmed {
  color: var(--color-green);
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;

  :deep(svg path) {
    stroke: currentColor;
    stroke-width: 1px;
  }
}

.status-respond {
  color: var(--color-pink);
}

.status-pending {
  color: var(--color-white-50);
}

.cardlet-cta {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: default;
}

.cta-circle :deep(.gam-button-container) {
  border-radius: 50% !important;
  width: 44px !important;
  height: 44px !important;
  min-width: 44px !important;
  padding: 0 !important;

  .icon {
    position: absolute !important;
    left: 50% !important;
    top: 50% !important;
    transform: translate(-50%, -50%) !important;
  }

  .svg-container {
    font-size: 22px !important;
    width: 22px !important;
    height: 22px !important;
  }
}

@include play-cta;
</style>
